/**
 * Stage-3 panel (s80 dashboard redesign): a single "Next up" pointer — the ONE
 * highest-priority incomplete thing (`computeNextSection`) — plus a compact
 * category progress grid. Never a list of next items; one pointer only.
 *
 * Renders secondary (muted) while essentials are still outstanding, and primary
 * (sage left accent) once they're all in place — at which point the pointer has
 * naturally moved past the essentials into the broader "complete the picture"
 * sections. The grid shows "X of Y sections" counts plus a per-category fractional
 * bar (P2 #3) driven by the shared `computeSectionProgress` rollup — the mean of each
 * section's [0,1] fraction (`categoryProgress.progressFraction`), so a full bar always
 * means all sections complete (never contradicts the "X/Y").
 */
import { Link } from "wouter";
import { useState } from "react";
import { ArrowRight, Check, Clock } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Client } from "@shared/schema";
import { useDashboardProgress } from "@/hooks/useDashboardProgress";
import { whyLineFor, estimateMinutes } from "@/lib/section-guidance";
import { MANUAL_NA_ELIGIBLE_SECTIONS } from "@shared/constants/section-gating";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { RequiredStar } from "@/components/RequiredFieldStar";
import { FONT, SAGE, WARM } from "@shared/brand";


export function NextUpCard({ clientData }: { clientData: Client }) {
  // F86-8b: session-only deferred set ("Come back later"). NOT persisted — resets on
  // unmount / full reload, so deferred items reappear on the next dashboard visit.
  const [deferred, setDeferred] = useState<Set<string>>(() => new Set());
  const { isLoading, essentialsComplete, next, allDeferred, planComplete, categoryProgress } =
    useDashboardProgress(clientData, deferred);

  // F86-8: "I don't have this" — one-tap mark the NEXT UP section N/A and advance.
  // Same write as the in-section button (POST /section-na, { sectionSlug }). Also
  // invalidates the ERS query so an N/A'd ESSENTIAL drops from `nextSteps` (the
  // essential pointer reads ERS, not just section-na). Section-na invalidation alone
  // covers the post-essentials section pointer; both together cover every case.
  const skipSection = useMutation({
    mutationFn: (sectionSlug: string) =>
      apiRequest("POST", `/api/clients/${clientData.id}/section-na`, { sectionSlug }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "section-na"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientData.id, "estate-ready-score"] });
    },
  });

  if (isLoading) return null;

  const primary = essentialsComplete;
  // The NEXT UP section's slug. Section pointers carry it directly; essential pointers
  // don't, so fall back to the `?section=` slug in the deep-link (parsed locally — NOT
  // assigned to `next.sectionSlug`, which must stay undefined for essentials so the
  // why-line below stays off). null when the target has no section surface.
  const naSlug = next ? (next.sectionSlug ?? next.deepLink.match(/[?&]section=([^&]+)/)?.[1] ?? null) : null;
  // Show the skip link only for sections the planner may opt out of (MANUAL_NA_ELIGIBLE).
  // Mandatory essentials (About You / Will / Executor …) are not eligible → no link.
  const canSkip = !!naSlug && MANUAL_NA_ELIGIBLE_SECTIONS.has(naSlug);
  // F86-8b: the identifier the current item is deferred by — its section slug when
  // present, else the deep-link (the one essential, key documents, has no section
  // surface). Always non-null when `next` exists, so "Come back later" works on EVERY
  // essential, including non-skippable ones. Mirrors the hook's `deferKeyOf`.
  const deferId = next ? (next.sectionSlug ?? next.deepLink.match(/[?&]section=([^&]+)/)?.[1] ?? next.deepLink) : null;
  const deferCurrent = () => {
    if (deferId) setDeferred((prev) => new Set(prev).add(deferId));
  };
  // Section pointers carry a why-line + a computed estimate; essentials carry neither.
  const whyLine = whyLineFor(next?.sectionSlug);
  const mins = estimateMinutes(next?.fieldCount);
  // s83 #10: the essential pointer AND the post-essentials section pointer are both an
  // active recommendation now — one "Do it now →" CTA for both. (The old "Fill it in →"
  // section branch was dead: section pointers only arise once essentials are complete,
  // a state the card previously dead-ended on without rendering `next`.)
  const ctaLabel = (
    <>
      Do it now <ArrowRight className="h-4 w-4" aria-hidden="true" />
    </>
  );

  return (
    <Card
      id="next-up"
      data-testid="next-up-card"
      className={primary ? "border-l-4 h-full" : "h-full"}
      style={primary ? { borderLeftColor: SAGE[600] } : { borderColor: WARM.stone }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Complete the picture</CardTitle>
            <p className="text-xs mt-0.5" style={{ color: WARM.taupe }}>
              {/* s176 (COMPLETE-STATE-1): the `primary` line read "keep filling in the
                  fuller picture" at 100% too, directly above the banner saying the
                  plan was finished. `planComplete` gets its own line. */}
              {planComplete
                ? "Nothing outstanding — here's the whole picture."
                : primary
                  ? "The essentials are done — keep filling in the fuller picture."
                  : "Everything beyond the 10 essentials, when you're ready."}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-5">
        {/* Polish (s88): order RESTORED — the "Next up" recommendation is the PRIMARY
            actionable surface and renders FIRST; the per-category progress grid follows
            below (reverts the s87 D-87.3 flip that buried the suggestion under the bars).
            computeNextSection points to the ONE highest-priority incomplete thing; the
            done/deferred states show their message here. Logic for all states unchanged. */}
        {allDeferred ? (
          /* F86-8b (Phase 3): everything outstanding was deferred this session. No
             "Do it now" — the items reappear on the next dashboard visit. */
          <div
            className="flex items-start gap-2.5 rounded-xl border p-4"
            style={{ borderColor: WARM.stone, backgroundColor: WARM.white }}
            data-testid="next-up-all-deferred"
          >
            <Clock className="h-4 w-4 mt-0.5 shrink-0" style={{ color: WARM.taupe }} />
            <p className="text-sm text-foreground">
              <span className="font-semibold">You've deferred the remaining essentials.</span>{" "}
              They'll be here when you're ready.
            </p>
          </div>
        ) : /* s176: the banner now renders off `planComplete` — the SAME value behind
               JourneyStageStrip's Stage-3 check, so the two surfaces cannot disagree.
               The `|| !next` disjunct is PURELY for type narrowing (it gives the else
               branch a non-null `next`): the `allDeferred` branch above has already
               returned, so at this point `planComplete` and `next === null` are
               provably the same condition, not two predicates. */
        planComplete || !next ? (
          <div
            className="flex items-start gap-2.5 rounded-xl border p-4"
            style={{ borderColor: SAGE[600], backgroundColor: SAGE[50] }}
            data-testid="next-up-all-complete"
          >
            <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: SAGE[600] }} />
            <p className="text-sm text-foreground">
              <span className="font-semibold">Your plan is complete.</span>{" "}
              Every section is filled in — your family and executor have the full picture.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {essentialsComplete && (
              <div
                className="flex items-start gap-2.5 rounded-xl border p-3"
                style={{ borderColor: SAGE[600], backgroundColor: SAGE[50] }}
                data-testid="next-up-essentials-done"
              >
                <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: SAGE[600] }} />
                <p className="text-sm text-foreground">
                  <span className="font-semibold">The essentials are in place</span> — your executor has what they need. Here's what to fill in next.
                </p>
              </div>
            )}
            <div
              className="rounded-lg border p-3"
              style={{ borderColor: WARM.stone, backgroundColor: WARM.white }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: WARM.taupe }}>
                Next Most Important Item
              </p>
              <p className="text-base font-semibold leading-tight text-foreground" style={{ fontFamily: FONT.serif }}>
                {next.label}
                {next.categoryName && (
                  <span className="text-sm font-normal ml-1.5" style={{ color: WARM.taupe }}>
                    · {next.categoryName}
                  </span>
                )}
              </p>
              {whyLine && (
                <p className="text-sm mt-1 leading-snug" style={{ color: WARM.taupe }}>
                  {whyLine}
                </p>
              )}
              {/* s87 (Bundle A): all actions on ONE horizontal row — primary sage CTA
                  + the two secondary bordered pills, wrapping gracefully on narrow.
                  "Skip for now" (F86-8b) defers for THIS session (no write); "Doesn't
                  apply to me" (F86-8) writes N/A — only for opt-out-eligible sections. */}
              <div className="mt-3 flex flex-row flex-wrap items-center gap-2">
                <Button asChild className="text-white hover:opacity-90" style={{ backgroundColor: SAGE[600] }}>
                  <Link href={next.deepLink} data-testid="next-up-pointer">
                    {ctaLabel}
                  </Link>
                </Button>
                <button
                  type="button"
                  onClick={deferCurrent}
                  className="inline-flex items-center whitespace-nowrap text-sm font-medium px-3 py-1.5 rounded-md border transition-colors hover:bg-[#F7F4EE]"
                  style={{ color: WARM.charcoal, borderColor: WARM.stone }}
                  data-testid="next-up-defer"
                >
                  Skip for now
                </button>
                {canSkip && (
                  <button
                    type="button"
                    onClick={() => skipSection.mutate(naSlug!)}
                    disabled={skipSection.isPending}
                    className="inline-flex items-center whitespace-nowrap text-sm font-medium px-3 py-1.5 rounded-md border transition-colors hover:bg-[#F7F4EE] disabled:opacity-50"
                    style={{ color: WARM.charcoal, borderColor: WARM.stone }}
                    data-testid="next-up-skip"
                  >
                    Doesn't apply to me
                  </button>
                )}
                {mins != null && (
                  <span className="text-xs ml-0.5" style={{ color: WARM.taupe }} data-testid="next-up-estimate">
                    ~{mins} min
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Per-category progress grid — secondary surface, below the "Next up" pointer
            (s88). Partial-credit bar = mean of each section's [0,1] fraction (P2 #3). */}
        {categoryProgress.length > 0 && (
          <div className="grid grid-cols-1 gap-2" data-testid="category-progress-grid">
            {categoryProgress.map((c) => {
              const allDone = c.total > 0 && c.complete === c.total;
              const pct = Math.round(c.progressFraction * 100);
              return (
                <Link
                  key={c.categoryId}
                  href={`/category/${c.categorySlug}`}
                  data-testid={`category-progress-${c.categorySlug}`}
                  className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors hover:bg-muted"
                  style={{ borderColor: WARM.stone }}
                >
                  <span
                    className="truncate font-medium"
                    style={{ color: allDone ? WARM.taupe : WARM.charcoal }}
                  >
                    {c.categoryName}
                  </span>
                  {allDone ? (
                    <Check className="h-4 w-4 shrink-0" style={{ color: SAGE[600] }} aria-label="Category complete" />
                  ) : (
                    <span className="shrink-0 flex items-center gap-2">
                      <span
                        className="h-2 w-20 rounded-full overflow-hidden"
                        style={{ backgroundColor: WARM.stone }}
                        aria-hidden="true"
                      >
                        <span
                          className="block h-full rounded-full transition-all"
                          style={{ width: `${pct}%`, backgroundColor: SAGE[600] }}
                        />
                      </span>
                      <span
                        className="font-medium tabular-nums w-8 text-right"
                        style={{ color: WARM.taupe }}
                        aria-label={`${c.complete} of ${c.total} sections complete`}
                      >
                        {c.complete}/{c.total}
                      </span>
                    </span>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        {/* s83: star legend — explains the `*` marker users see inside categories.
            Footnote at the very bottom of the card (Next up sits above the grid, s88). */}
        <p className="text-[11px] inline-flex items-center" style={{ color: WARM.taupe }}>
          Fields marked <RequiredStar className="text-base" /> count toward completion.
        </p>
      </CardContent>
    </Card>
  );
}
